/**
 * `/settings/branches` — **Branches** (C11, FR-A8 §8, FR-A6; T-1.19).
 *
 * A table — code, name, address and state, default, status — with **Add
 * branch** at the top right; add and edit in a dialog; **Deactivate** behind a
 * confirmation and **Reactivate** for an inactive row; a filter for active,
 * inactive or all.
 *
 * Two refusals are the service's and are said here in the firm's words:
 * the **default branch cannot be deactivated** (its button is disabled, with
 * the reason beside it), and **reactivating refuses a code an active branch
 * already holds** (the service's `CONFLICT`, shown as it comes back).
 *
 * *Deactivate* and *Reactivate* are the screen's words for the service's
 * `archive` and `restore`. A branch is never deleted: it names itself on old
 * cases, invoices and reports.
 */

import {
  Alert,
  Badge,
  Button,
  Checkbox,
  ConfirmDialog,
  DataTable,
  Dialog,
  EmptyState,
  Input,
  Select,
  Tabs,
  Textarea,
  type ColumnDef,
} from '@fiveox/ui'
import { GitBranch, Pencil, Plus, Power, RotateCcw } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { SETTINGS, useEvaluation } from '../../../lib/access'
import { ApiError } from '../../../lib/api'
import { type Branch, firm } from '../../../lib/api/firm'
import { STATES } from './rules'
import { SettingsPage } from './SettingsPage'

type Filter = 'active' | 'archived' | 'all'

interface BranchForm {
  code: string
  name: string
  address: string
  state: string
  is_default: boolean
}

const EMPTY: BranchForm = { code: '', name: '', address: '', state: '', is_default: false }

export function filterBranches(rows: Branch[], filter: Filter): Branch[] {
  return filter === 'all' ? rows : rows.filter((row) => row.status === filter)
}

function BranchDialog({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean
  editing: Branch | null
  onClose: () => void
  onSaved: () => Promise<void>
}) {
  const [form, setForm] = useState<BranchForm>(EMPTY)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setError(null)
    setForm(
      editing
        ? {
            code: editing.code,
            name: editing.name,
            address: editing.address ?? '',
            state: editing.state ?? '',
            is_default: editing.is_default,
          }
        : EMPTY,
    )
  }, [open, editing])

  const save = async () => {
    setBusy(true)
    setError(null)
    const body = {
      code: form.code.trim().toUpperCase(),
      name: form.name.trim(),
      address: form.address.trim() || null,
      state: form.state || null,
      is_default: form.is_default,
    }
    try {
      if (editing) await firm.updateBranch(editing.id, body)
      else await firm.createBranch(body)
      await onSaved()
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That branch could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  const ready = form.code.trim() !== '' && form.name.trim() !== ''

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={editing ? `Edit ${editing.code}` : 'Add a branch'}
      error={error}
      width="lg"
    >
      <div className="grid gap-4 sm:grid-cols-[10rem_minmax(0,1fr)]">
        <Input
          label="Code"
          value={form.code}
          maxLength={16}
          className="font-mono tracking-wide uppercase"
          onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
          hint="Short, your own — MUM, PUN."
        />
        <Input
          label="Name"
          value={form.name}
          maxLength={120}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <div className="sm:col-span-2">
          <Textarea
            label="Address"
            rows={2}
            maxLength={400}
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
          />
        </div>
        <div className="sm:col-span-2">
          <Select
            label="State or union territory"
            value={form.state}
            onChange={(e) => setForm({ ...form, state: e.target.value })}
            hint="A branch in another state is usually why a firm holds a second GSTIN."
          >
            <option value="">Not given</option>
            {STATES.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Checkbox
            checked={form.is_default}
            disabled={editing?.is_default}
            onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
            label="The default branch"
            hint={
              editing?.is_default
                ? 'This is your default. Make another branch the default to change it.'
                : 'What a new client or case gets when nobody chose. There is one default at a time.'
            }
          />
        </div>
      </div>
      <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={() => void save()} disabled={!ready} loading={busy}>
          {editing ? 'Save branch' : 'Add branch'}
        </Button>
      </div>
    </Dialog>
  )
}

export function Branches() {
  const { can } = useEvaluation()
  const editable = can(SETTINGS, 'can_update')
  const [rows, setRows] = useState<Branch[] | null>(null)
  const [filter, setFilter] = useState<Filter>('active')
  const [dialog, setDialog] = useState<{ open: boolean; editing: Branch | null }>({
    open: false,
    editing: null,
  })
  const [confirming, setConfirming] = useState<Branch | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setRows((await firm.branches('all')).data)
    } catch {
      setError('Your branches could not be loaded.')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const act = async (row: Branch, kind: 'deactivate' | 'reactivate') => {
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      if (kind === 'deactivate') await firm.archiveBranch(row.id)
      else await firm.restoreBranch(row.id)
      setNotice(kind === 'deactivate' ? `${row.code} is inactive.` : `${row.code} is active again.`)
      setConfirming(null)
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That could not be done.')
      setConfirming(null)
    } finally {
      setBusy(false)
    }
  }

  const counts = useMemo(() => {
    const all = rows ?? []
    return {
      active: all.filter((r) => r.status === 'active').length,
      archived: all.filter((r) => r.status === 'archived').length,
      all: all.length,
    }
  }, [rows])

  const columns: ColumnDef<Branch>[] = [
    {
      key: 'code',
      label: 'Code',
      width: 90,
      required: true,
      render: (row) => <span className="text-foreground font-mono font-semibold">{row.code}</span>,
    },
    {
      key: 'name',
      label: 'Name',
      width: 180,
      render: (row) => (
        <span className="flex items-center gap-2">
          <span className="text-foreground font-medium">{row.name}</span>
          {row.is_default && (
            <Badge tone="info" size="sm">
              Default
            </Badge>
          )}
        </span>
      ),
    },
    {
      key: 'where',
      label: 'Address · state',
      width: 240,
      render: (row) => (
        <span className="block max-w-xs">
          {row.address && <span className="text-foreground block truncate">{row.address}</span>}
          <span className="text-muted-foreground">{row.state ?? (row.address ? '' : '—')}</span>
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: 100,
      render: (row) =>
        row.status === 'active' ? (
          <Badge tone="success" size="sm">
            Active
          </Badge>
        ) : (
          <Badge tone="neutral" size="sm">
            Inactive
          </Badge>
        ),
    },
  ]

  const visible = filterBranches(rows ?? [], filter)

  return (
    <SettingsPage
      title="Branches"
      description="Your offices. Clients, staff and cases carry one and reports split by it — a branch never limits who sees what."
      actions={
        editable ? (
          <Button onClick={() => setDialog({ open: true, editing: null })}>
            <Plus size={16} aria-hidden /> Add branch
          </Button>
        ) : undefined
      }
    >
      {error && <Alert tone="error">{error}</Alert>}
      {notice && !error && <Alert tone="success">{notice}</Alert>}

      {rows !== null && rows.length === 0 ? (
        <EmptyState
          title="No branches yet"
          description="Add your first office. It becomes the default — what a new client or case gets when nobody chooses."
          action={
            editable ? (
              <Button onClick={() => setDialog({ open: true, editing: null })}>
                <GitBranch size={16} aria-hidden /> Add your first branch
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="space-y-4">
          <Tabs
            idBase="branches"
            active={filter}
            onChange={(id) => setFilter(id as Filter)}
            tabs={[
              { id: 'active', label: 'Active', badge: counts.active },
              { id: 'archived', label: 'Inactive', badge: counts.archived },
              { id: 'all', label: 'All', badge: counts.all },
            ]}
          />
          <DataTable
            columns={columns}
            rows={visible}
            rowKey={(row) => row.id}
            loading={rows === null}
            empty={
              filter === 'archived' ? 'No inactive branches.' : 'No branches match this filter.'
            }
            trailing={
              editable
                ? {
                    header: 'Actions',
                    width: 200,
                    cell: (row) => (
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setDialog({ open: true, editing: row })}
                          aria-label={`Edit ${row.code}`}
                        >
                          <Pencil size={14} aria-hidden /> Edit
                        </Button>
                        {row.status === 'active' ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={row.is_default}
                            title={
                              row.is_default
                                ? 'Your default branch can’t be deactivated. Make another branch the default first.'
                                : undefined
                            }
                            onClick={() => setConfirming(row)}
                          >
                            <Power size={14} aria-hidden /> Deactivate
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={busy}
                            onClick={() => void act(row, 'reactivate')}
                          >
                            <RotateCcw size={14} aria-hidden /> Reactivate
                          </Button>
                        )}
                      </div>
                    ),
                  }
                : undefined
            }
          />
          {rows?.some((row) => row.is_default && row.status === 'active') && editable && (
            <p className="text-muted-foreground text-xs">
              Your default branch can’t be deactivated — make another branch the default first.
            </p>
          )}
        </div>
      )}

      <BranchDialog
        open={dialog.open}
        editing={dialog.editing}
        onClose={() => setDialog({ open: false, editing: null })}
        onSaved={load}
      />

      <ConfirmDialog
        open={confirming !== null}
        onClose={() => setConfirming(null)}
        title={confirming ? `Deactivate ${confirming.code}?` : 'Deactivate'}
        confirmLabel="Deactivate"
        tone="danger"
        busy={busy}
        onConfirm={() => confirming && void act(confirming, 'deactivate')}
      >
        <p className="text-muted-foreground text-sm">
          {confirming?.name} stops being offered for new clients and cases. Everything already
          filed under it keeps its branch, and you can reactivate it at any time.
        </p>
      </ConfirmDialog>
    </SettingsPage>
  )
}
